import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/shared/StatusBadge";
import * as Icons from "lucide-react";
import { useCollection } from "@/hooks/useCollection";
import { inr, text, fmtTs } from "@/lib/format";

const PURPOSE = { start_project: "Start a Project fee", materials: "Materials", wallet_topup: "Wallet top-up" };

export const LivePayments = () => {
  const recs = useCollection("cc_payment_records");
  const requests = useCollection("requests");
  const users = useCollection("users");
  const products = useCollection("products");
  const [q, setQ] = useState("");
  const [mode, setMode] = useState("all");

  const reqById = useMemo(() => Object.fromEntries(requests.map((r) => [r.id, r])), [requests]);
  const userBy = useMemo(() => {
    const m = {};
    users.forEach((u) => { m[u.id] = u; if (u.phone) m[String(u.phone).slice(-10)] = u; });
    return m;
  }, [users]);
  const prodById = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);

  const rows = useMemo(() => recs.map((p) => {
    const r = reqById[p.payment_ref] || {};
    const u = userBy[p.payer_uid] || userBy[String(p.payer_phone || p.contact || "").slice(-10)] || {};
    const items = (p.items || []).map((i) => `${prodById[i.product_id]?.name || i.product_id} × ${i.qty}`).join(", ");
    const service = p.purpose === "materials" ? items : (r.subcategory || r.category_name || (Array.isArray(r.services) ? r.services.join(", ") : r.category));
    return { ...p, customer: p.payer_name || u.name || u.full_name || r.customer_name, phone: p.payer_phone || p.contact || u.phone, service, city: r.city, leadCode: r.lead_code };
  }).sort((a, b) => String(b.created_at).localeCompare(String(a.created_at))), [recs, reqById, userBy, prodById]);

  const shown = rows.filter((p) => (mode === "all" || p.mode === mode) && (!q || JSON.stringify(p).toLowerCase().includes(q.toLowerCase())));
  const ok = (p) => ["verified", "captured"].includes(p.status);
  const liveTotal = rows.filter((p) => p.mode === "live" && ok(p)).reduce((s, p) => s + (Number(p.amount) || 0), 0) / 100;

  return (
    <Card className="mb-6" data-testid="live-payments">
      <div className="p-4 border-b border-border flex flex-wrap items-center gap-3">
        <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" /></span>
        <h3 className="font-heading font-semibold text-sm">Razorpay payments · real time</h3>
        <span className="text-xs text-muted-foreground" data-testid="live-pay-total">Live collected: <b className="text-foreground">{inr(liveTotal)}</b></span>
        <div className="ml-auto flex items-center gap-2">
          {["all", "live", "test"].map((m) => (
            <button key={m} data-testid={`live-pay-filter-${m}`} onClick={() => setMode(m)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${mode === m ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-muted"}`}>{m.toUpperCase()}</button>
          ))}
          <div className="relative w-56">
            <Icons.Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input data-testid="live-pay-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order, customer…" className="h-8 pl-8 text-xs" />
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>When</TableHead><TableHead>Purchased</TableHead><TableHead>Service / product</TableHead><TableHead>Customer</TableHead>
            <TableHead>Amount</TableHead><TableHead>Method</TableHead><TableHead>Order / Payment</TableHead><TableHead>Mode</TableHead><TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {shown.map((p) => (
              <TableRow key={p.id} data-testid={`live-pay-${p.id}`}>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtTs(p.verified_at || p.created_at)}</TableCell>
                <TableCell className="text-sm">{PURPOSE[p.purpose] || text(p.purpose)}</TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-[220px] truncate" title={p.service || ""}>
                  {text(p.service)}{p.leadCode && <span className="ml-1 font-mono text-[10px]">#{p.leadCode}</span>}{p.city && <span className="block text-[10px]">{p.city}</span>}
                </TableCell>
                <TableCell className="text-sm">{text(p.customer)}<span className="block text-[10px] text-muted-foreground font-mono">{text(p.phone, "")}{p.email ? ` · ${p.email}` : ""}</span></TableCell>
                <TableCell className="font-mono">{inr((Number(p.amount) || 0) / 100)}</TableCell>
                <TableCell className="text-xs capitalize text-muted-foreground">{text(p.method)}{p.card_network ? ` · ${p.card_network}` : ""}{p.vpa ? ` · ${p.vpa}` : ""}{p.bank ? ` · ${p.bank}` : ""}{p.wallet ? ` · ${p.wallet}` : ""}</TableCell>
                <TableCell className="font-mono text-[10px] text-muted-foreground">{p.order_id || p.id}<span className="block">{text(p.payment_id, "")}</span></TableCell>
                <TableCell><span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${p.mode === "live" ? "bg-emerald-500/15 text-emerald-500" : "bg-amber-500/15 text-amber-500"}`}>{text(p.mode).toUpperCase()}</span></TableCell>
                <TableCell><StatusBadge status={p.status || "created"} /></TableCell>
              </TableRow>
            ))}
            {shown.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground" data-testid="live-pay-empty">No Razorpay payments yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
};
